import { assertEvent, assign, fromPromise, setup } from 'xstate'
import { findLastIndex } from 'lodash-es'
import {
  CreateUserTermStatusResponse,
  OrderEstimate,
  OrderEstimateMealSelection,
  SelectionResponse,
  TermStatus,
} from '@tovala/browser-apis-combinedapi'

import { SelectionEventSourceID } from 'types/internal'

interface ListingMeta {
  listingID: string
  selectionEventSourceID: SelectionEventSourceID
}

interface MenuActors {
  addListing: { data: SelectionResponse }
  addMeal: { data: SelectionResponse }
  removeListing: { data: SelectionResponse }
  removeMeal: { data: SelectionResponse }
  savePendingMeal: { data: SelectionResponse }
  updateTermStatus: { data: TermStatus }
}

interface MealMeta {
  mealID: number
  mealIndex: number | undefined
  selectionEventSourceID: SelectionEventSourceID
}

interface MenuContext {
  addListingMeta: ListingMeta | undefined
  addMealMeta: MealMeta | undefined
  nextMaxSelections: number | undefined
  nextPendingMealIndex: number
  pendingMealIDs: number[]
  pendingMealIndices: (number | undefined)[]
  removeListingMeta: ListingMeta | undefined
  removeMealMeta:
    | (MealMeta & {
        maxSelections: number
      })
    | undefined
  subTermID: string
  termID: number
  userID: number
}

type MenuEvents =
  | {
      listingID: string
      selectionEventSourceID: SelectionEventSourceID
      type: 'ADD_LISTING'
    }
  | {
      mealID: number
      mealIndex: number | undefined
      nextMaxSelections: number | undefined
      selectionEventSourceID: SelectionEventSourceID
      type: 'ADD_MEAL'
    }
  | { type: 'ALL_MEALS_SELECTED' }
  | { type: 'CANCEL_UPGRADE' }
  | { type: 'NOT_ALL_MEALS_SELECTED' }
  | {
      listingID: string
      selectionEventSourceID: SelectionEventSourceID
      type: 'REMOVE_LISTING'
    }
  | {
      maxSelections: number
      mealID: number
      mealIndex: number | undefined
      selectionEventSourceID: SelectionEventSourceID
      type: 'REMOVE_MEAL'
    }
  | { type: 'SAVE_PENDING_MEALS' }
  | {
      nextMaxSelections: number | undefined
      type: 'UPGRADE'
    }

export const menuMachine = setup({
  types: {} as {
    actors: MenuActors
    context: MenuContext
    events: MenuEvents
    input: {
      subTermID: string
      termID: number
      userID: number
    }
  },
  actions: {
    addToPendingMealIDs: assign({
      pendingMealIDs: (
        { context },
        params: { mealID: number; mealIndex: number | undefined },
      ) => {
        return [...context.pendingMealIDs, params.mealID]
      },
      pendingMealIndices: (
        { context },
        params: { mealID: number; mealIndex: number | undefined },
      ) => {
        return [...context.pendingMealIndices, params.mealIndex]
      },
    }),
    clearAddListingMeta: assign({ addListingMeta: undefined }),
    clearAddMealMeta: assign({ addMealMeta: undefined }),
    clearRemoveMealMeta: assign({ removeMealMeta: undefined }),
    clearRemoveListingMeta: assign({ removeListingMeta: undefined }),
    clearPendingMealIDs: assign({
      pendingMealIDs: [],
      pendingMealIndices: [],
    }),
    clearNextMaxSelections: assign({
      nextMaxSelections: undefined,
    }),
    incrementNextPendingMealIndex: assign({
      nextPendingMealIndex: ({ context }) => {
        return context.nextPendingMealIndex + 1
      },
    }),
    removeFirstPendingMeal: assign({
      pendingMealIDs: ({ context }) => {
        return context.pendingMealIDs.slice(1)
      },
      pendingMealIndices: ({ context }) => {
        return context.pendingMealIndices.slice(1)
      },
    }),
    removeFromPendingMealIDs: assign(
      ({ context }, params: { mealID: number }) => {
        const newPendingMealIDs = [...context.pendingMealIDs]
        const newPendingMealIndices = [...context.pendingMealIndices]

        const lastIndexOfMealID = findLastIndex(
          newPendingMealIDs,
          (pendingMealID) => {
            return pendingMealID === params.mealID
          },
        )

        if (lastIndexOfMealID !== -1) {
          newPendingMealIDs.splice(lastIndexOfMealID, 1)
          newPendingMealIndices.splice(lastIndexOfMealID, 1)
        }

        return {
          pendingMealIDs: newPendingMealIDs,
          pendingMealIndices: newPendingMealIndices,
        }
      },
    ),
    setNextPendingMealIndex: assign({
      nextPendingMealIndex: 0,
    }),
    setAddMealMetaFromPending: assign({
      addMealMeta: ({ context }) => {
        return {
          mealID: context.pendingMealIDs[context.nextPendingMealIndex],
          mealIndex: context.pendingMealIndices[context.nextPendingMealIndex],
          selectionEventSourceID: 'menu_term',
        }
      },
    }),
    storeAddListingMeta: assign({
      addListingMeta: (
        _,
        params: {
          listingID: string
          selectionEventSourceID: SelectionEventSourceID
        },
      ) => {
        return {
          listingID: params.listingID,
          selectionEventSourceID: params.selectionEventSourceID,
        }
      },
    }),
    storeAddMealMeta: assign({
      addMealMeta: (
        _,
        params: {
          mealID: number
          mealIndex: number | undefined
          selectionEventSourceID: SelectionEventSourceID
        },
      ) => {
        return {
          mealID: params.mealID,
          mealIndex: params.mealIndex,
          selectionEventSourceID: params.selectionEventSourceID,
        }
      },
    }),
    storeNextMaxSelections: assign({
      nextMaxSelections: (
        _,
        params: { nextMaxSelections: number | undefined },
      ) => {
        return params.nextMaxSelections
      },
    }),
    storeRemoveListingMeta: assign({
      removeListingMeta: (
        _,
        params: {
          listingID: string
          selectionEventSourceID: SelectionEventSourceID
        },
      ) => {
        return {
          listingID: params.listingID,
          selectionEventSourceID: params.selectionEventSourceID,
        }
      },
    }),
    storeRemoveMealMeta: assign({
      removeMealMeta: (
        _,
        params: {
          mealID: number
          mealIndex: number | undefined
          maxSelections: number
          selectionEventSourceID: SelectionEventSourceID
        },
      ) => {
        return {
          maxSelections: params.maxSelections,
          mealID: params.mealID,
          mealIndex: params.mealIndex,
          selectionEventSourceID: params.selectionEventSourceID,
        }
      },
    }),
    // Stubbed actions
    showAddListingError: (_, params: { data: unknown }) => {
      throw new Error(
        `showAddListingError not implemented. Expected params: ${Object.keys(
          params,
        )}`,
      )
    },
    showAddMealError: (_, params: { data: unknown }) => {
      throw new Error(
        `showAddMealError not implemented. Expected params: ${Object.keys(
          params,
        )}`,
      )
    },
    showRemoveListingError: () => {
      throw new Error('showRemoveListingError not implemented')
    },
    showRemoveMealError: () => {
      throw new Error('showRemoveMealError not implemented')
    },
    trackConfirmedUpgrade: () => {
      throw new Error('trackConfirmedUpgrade not implemented')
    },
    trackFinishedMealSelection: (_, params: { termID: number }) => {
      throw new Error(
        `trackFinishedMealSelection not implemented. Expected params: ${Object.keys(
          params,
        )}`,
      )
    },
    trackMealAdded: (
      _,
      params: {
        termID: number
        addMealMeta: MealMeta | undefined
        orderEstimate: OrderEstimate
      },
    ) => {
      throw new Error(
        `trackMealAdded not implemented. Expected params: ${Object.keys(
          params,
        )}`,
      )
    },
    trackMealRemoved: (
      _,
      params: {
        removeMealMeta: (MealMeta & { maxSelections: number }) | undefined
        termID: number
        mealSelections: OrderEstimateMealSelection[]
      },
    ) => {
      throw new Error(
        `trackMealRemoved not implemented. Expected params: ${Object.keys(
          params,
        )}`,
      )
    },
  },
  guards: {
    hasMorePendingMeals: ({ context }) => {
      return context.nextPendingMealIndex + 1 < context.pendingMealIDs.length
    },
    hasPendingMealIDs: ({ context }) => {
      return context.pendingMealIDs.length > 0
    },
    isLastMealIDToRemovePending: ({ context }, params: { mealID: number }) => {
      return (
        context.pendingMealIDs.includes(params.mealID) &&
        context.pendingMealIDs.length === 1
      )
    },
    isLastPendingMeal: ({ context }) => {
      return context.pendingMealIDs.length === 1
    },
    isMealIDToRemovePending: ({ context }, params: { mealID: number }) => {
      return context.pendingMealIDs.includes(params.mealID)
    },
    // Stubbed guards
    hasFewerMealsThanNextPlanMax: () => {
      throw new Error('hasFewerMealsThanNextPlanMax not implemented')
    },
    hasFewerThanMaxMeals: () => {
      throw new Error('hasFewerThanMaxMeals not implemented')
    },
    hasMaxMeals: (_, params: { orderEstimate: OrderEstimate }) => {
      throw new Error(
        `hasMaxMeals not implemented. Expected params: ${Object.keys(params)}`,
      )
    },
    hasSelectedAllMeals: () => {
      throw new Error('hasSelectedAllMeals not implemented')
    },
  },
  actors: {
    // Stubbed actors
    addListing: fromPromise<
      SelectionResponse,
      {
        listingID: string
        userID: number
      }
    >(async () => {
      throw new Error('addListing not implemented')
    }),
    addMeal: fromPromise<
      SelectionResponse,
      {
        mealID: number
        subTermID: string
        userID: number
      }
    >(async () => {
      throw new Error('addMeal not implemented')
    }),
    removeListing: fromPromise<
      SelectionResponse,
      {
        listingID: string
        userID: number
      }
    >(async () => {
      throw new Error('removeListing not implemented')
    }),
    removeMeal: fromPromise<
      SelectionResponse,
      {
        mealID: number
        subTermID: string
        userID: number
      }
    >(async () => {
      throw new Error('removeMeal not implemented')
    }),
    updateTermStatus: fromPromise<CreateUserTermStatusResponse>(async () => {
      throw new Error('updateTermStatus not implemented')
    }),
    savePendingMeal: fromPromise<
      SelectionResponse,
      {
        pendingMealIDs: number[]
        nextPendingMealIndex: number
      }
    >(async () => {
      throw new Error('updateTermStatus not implemented')
    }),
  },
}).createMachine({
  context: ({ input }) => ({
    addListingMeta: undefined,
    addMealMeta: undefined,
    nextPendingMealIndex: 0,
    nextMaxSelections: undefined,
    pendingMealIDs: [],
    pendingMealIndices: [],
    removeListingMeta: undefined,
    removeMealMeta: undefined,
    selectionSourceID: undefined,
    subTermID: input.subTermID,
    termID: input.termID,
    userID: input.userID,
  }),
  id: 'menuMachine',
  initial: 'checkingSelectionsState',
  states: {
    checkingSelectionsState: {
      always: [
        {
          guard: 'hasSelectedAllMeals',
          target: 'mealsSelected',
        },
        { target: 'idle' },
      ],
    },
    idle: {
      on: {
        ADD_LISTING: {
          actions: [
            {
              type: 'storeAddListingMeta',
              params: ({ event }) => ({
                listingID: event.listingID,
                selectionEventSourceID: event.selectionEventSourceID,
              }),
            },
          ],
          target: 'addingListing',
        },
        ADD_MEAL: {
          actions: [
            {
              type: 'storeAddMealMeta',
              params: ({ event }) => ({
                mealID: event.mealID,
                mealIndex: event.mealIndex,
                selectionEventSourceID: event.selectionEventSourceID,
              }),
            },
          ],
          target: 'addingMeal',
        },
        ALL_MEALS_SELECTED: { target: 'mealsSelected' },
        REMOVE_LISTING: {
          actions: [
            {
              type: 'storeRemoveListingMeta',
              params: ({ event }) => ({
                listingID: event.listingID,
                selectionEventSourceID: event.selectionEventSourceID,
              }),
            },
          ],
          target: 'removingListing',
        },
        REMOVE_MEAL: {
          actions: [
            {
              type: 'storeRemoveMealMeta',
              params: ({ event }) => ({
                mealID: event.mealID,
                mealIndex: event.mealIndex,
                maxSelections: event.maxSelections,
                selectionEventSourceID: event.selectionEventSourceID,
              }),
            },
          ],
          target: 'removingMeal',
        },
      },
    },
    addingListing: {
      invoke: {
        src: 'addListing',
        input: ({ context, event }) => {
          assertEvent(event, 'ADD_LISTING')

          return {
            listingID: event.listingID,
            userID: context.userID,
          }
        },
        onDone: [
          {
            actions: [
              {
                type: 'clearAddListingMeta',
              },
            ],
            target: '#menuMachine.upgrading.selectingUpgrades',
            guard: 'hasPendingMealIDs',
          },
          {
            actions: [
              {
                type: 'clearAddListingMeta',
              },
            ],
            target: '#menuMachine.checkingSelectionsState',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'showAddListingError',
                params: ({ event }) => ({ data: event.error }),
              },
            ],
            target: '#menuMachine.upgrading.selectingUpgrades',
            guard: 'hasPendingMealIDs',
          },
          {
            actions: [
              {
                type: 'showAddListingError',
                params: ({ event }) => ({ data: event.error }),
              },
            ],
            target: '#menuMachine.checkingSelectionsState',
          },
        ],
      },
    },
    addingMeal: {
      invoke: {
        src: 'addMeal',
        input: ({ context, event }) => {
          assertEvent(event, 'ADD_MEAL')
          return {
            mealID: event.mealID,
            subTermID: context.subTermID,
            userID: context.userID,
          }
        },
        onDone: [
          {
            actions: [
              {
                type: 'trackMealAdded',
                params: ({ context, event }) => ({
                  addMealMeta: context.addMealMeta,
                  termID: context.termID,
                  orderEstimate: event.output,
                }),
              },
              {
                type: 'trackFinishedMealSelection',
                params: ({ context }) => ({ termID: context.termID }),
              },
              'clearAddMealMeta',
            ],
            guard: {
              type: 'hasMaxMeals',
              params: ({ event }) => ({ orderEstimate: event.output }),
            },
            target: 'mealsSelected',
          },
          {
            actions: [
              {
                type: 'trackMealAdded',
                params: ({ context, event }) => ({
                  addMealMeta: context.addMealMeta,
                  termID: context.termID,
                  orderEstimate: event.output,
                }),
              },
              'clearAddMealMeta',
            ],
            target: 'idle',
          },
        ],
        onError: {
          actions: [
            {
              type: 'showAddMealError',
              params: ({ event }) => ({ data: event.error }),
            },
          ],
          target: 'idle',
        },
      },
    },
    removingListing: {
      invoke: {
        src: 'removeListing',
        input: ({ context, event }) => {
          assertEvent(event, 'REMOVE_LISTING')
          return {
            listingID: event.listingID,
            userID: context.userID,
          }
        },
        onDone: [
          {
            actions: [
              {
                type: 'clearRemoveListingMeta',
              },
            ],
            target: '#menuMachine.upgrading.selectingUpgrades',
            guard: 'hasPendingMealIDs',
          },
          {
            actions: [
              {
                type: 'clearRemoveListingMeta',
              },
            ],
            target: '#menuMachine.checkingSelectionsState',
          },
        ],
        onError: [
          {
            actions: ['showRemoveListingError'],
            target: '#menuMachine.upgrading.selectingUpgrades',
            guard: 'hasPendingMealIDs',
          },
          {
            actions: ['showRemoveListingError'],
            target: '#menuMachine.checkingSelectionsState',
          },
        ],
      },
    },
    removingMeal: {
      invoke: {
        src: 'removeMeal',
        input: ({ context, event }) => {
          assertEvent(event, 'REMOVE_MEAL')
          return {
            mealID: event.mealID,
            subTermID: context.subTermID,
            userID: context.userID,
          }
        },
        onDone: {
          actions: [
            {
              type: 'trackMealRemoved',
              params: ({ context, event }) => ({
                removeMealMeta: context.removeMealMeta,
                termID: context.termID,
                mealSelections: event.output.mealSelections,
              }),
            },
            'clearRemoveMealMeta',
          ],
          target: 'idle',
        },
        onError: { actions: ['showRemoveMealError'], target: 'idle' },
      },
    },
    mealsSelected: {
      on: {
        ADD_LISTING: {
          actions: [
            {
              type: 'storeAddListingMeta',
              params: ({ event }) => ({
                listingID: event.listingID,
                selectionEventSourceID: event.selectionEventSourceID,
              }),
            },
          ],
          target: 'addingListing',
        },
        ADD_MEAL: {
          actions: [
            {
              type: 'addToPendingMealIDs',
              params: ({ event }) => ({
                mealID: event.mealID,
                mealIndex: event.mealIndex,
              }),
            },
            {
              type: 'storeNextMaxSelections',
              params: ({ event }) => ({
                nextMaxSelections: event.nextMaxSelections,
              }),
            },
          ],
          guard: 'hasFewerThanMaxMeals',
          target: 'upgrading',
        },
        NOT_ALL_MEALS_SELECTED: { target: 'checkingSelectionsState' },
        REMOVE_LISTING: {
          actions: [
            {
              type: 'storeRemoveListingMeta',
              params: ({ event }) => ({
                listingID: event.listingID,
                selectionEventSourceID: event.selectionEventSourceID,
              }),
            },
          ],
          target: 'removingListing',
        },
        REMOVE_MEAL: {
          actions: [
            {
              type: 'storeRemoveMealMeta',
              params: ({ event }) => ({
                mealID: event.mealID,
                mealIndex: event.mealIndex,
                selectionEventSourceID: event.selectionEventSourceID,
                maxSelections: event.maxSelections,
              }),
            },
          ],
          target: 'removingMeal',
        },
        UPGRADE: {
          actions: [
            {
              type: 'storeNextMaxSelections',
              params: ({ event }) => ({
                nextMaxSelections: event.nextMaxSelections,
              }),
            },
          ],
          target: 'upgrading',
        },
      },
    },
    upgrading: {
      initial: 'selectingUpgrades',
      states: {
        selectingUpgrades: {
          on: {
            ADD_LISTING: {
              actions: [
                {
                  type: 'storeAddListingMeta',
                  params: ({ event }) => ({
                    listingID: event.listingID,
                    selectionEventSourceID: event.selectionEventSourceID,
                  }),
                },
              ],
              target: '#menuMachine.addingListing',
            },
            ADD_MEAL: {
              actions: [
                {
                  type: 'addToPendingMealIDs',
                  params: ({ event }) => ({
                    mealID: event.mealID,
                    mealIndex: event.mealIndex,
                  }),
                },
              ],
              guard: 'hasFewerMealsThanNextPlanMax',
            },
            CANCEL_UPGRADE: {
              actions: ['clearPendingMealIDs', 'clearNextMaxSelections'],
              target: '#menuMachine.checkingSelectionsState',
            },
            REMOVE_LISTING: {
              actions: [
                {
                  type: 'storeRemoveListingMeta',
                  params: ({ event }) => ({
                    listingID: event.listingID,
                    selectionEventSourceID: event.selectionEventSourceID,
                  }),
                },
              ],
              target: '#menuMachine.removingListing',
            },
            REMOVE_MEAL: [
              {
                actions: [
                  {
                    type: 'removeFromPendingMealIDs',
                    params: ({ event }) => ({
                      mealID: event.mealID,
                    }),
                  },
                  'clearNextMaxSelections',
                ],
                guard: {
                  type: 'isLastMealIDToRemovePending',
                  params: ({ event }) => ({
                    mealID: event.mealID,
                  }),
                },
                target: '#menuMachine.checkingSelectionsState',
              },
              {
                actions: [
                  {
                    type: 'removeFromPendingMealIDs',
                    params: ({ event }) => ({
                      mealID: event.mealID,
                    }),
                  },
                ],
                guard: {
                  type: 'isMealIDToRemovePending',
                  params: ({ event }) => ({
                    mealID: event.mealID,
                  }),
                },
              },
              {
                actions: [
                  {
                    type: 'storeRemoveMealMeta',
                    params: ({ event }) => ({
                      mealID: event.mealID,
                      mealIndex: event.mealIndex,
                      selectionEventSourceID: event.selectionEventSourceID,
                      maxSelections: event.maxSelections,
                    }),
                  },
                ],
                target: 'removingMeal',
              },
            ],
            SAVE_PENDING_MEALS: {
              actions: ['trackConfirmedUpgrade'],
              target: 'savingUpgrades',
            },
          },
        },
        removingMeal: {
          initial: 'removing',
          states: {
            removing: {
              invoke: {
                src: 'removeMeal',
                input: ({ context, event }) => {
                  assertEvent(event, 'REMOVE_MEAL')
                  return {
                    mealID: event.mealID,
                    subTermID: context.subTermID,
                    userID: context.userID,
                  }
                },
                onDone: [
                  {
                    actions: [
                      {
                        type: 'trackMealRemoved',
                        params: ({ context, event }) => ({
                          removeMealMeta: context.removeMealMeta,
                          termID: context.termID,
                          mealSelections: event.output.mealSelections,
                        }),
                      },
                      'clearRemoveMealMeta',
                      'setNextPendingMealIndex',
                    ],
                    guard: 'hasPendingMealIDs',
                    target: 'addingMealFromPending',
                  },
                  {
                    actions: [
                      {
                        type: 'trackMealRemoved',
                        params: ({ context, event }) => ({
                          removeMealMeta: context.removeMealMeta,
                          termID: context.termID,
                          mealSelections: event.output.mealSelections,
                        }),
                      },
                      'clearRemoveMealMeta',
                    ],
                    target: '#menuMachine.idle',
                  },
                ],
                onError: {
                  actions: ['showRemoveMealError'],
                  target: '#menuMachine.upgrading.selectingUpgrades',
                },
              },
            },
            addingMealFromPending: {
              entry: ['setAddMealMetaFromPending'],
              invoke: {
                src: 'savePendingMeal',
                input: ({ context }) => {
                  return {
                    pendingMealIDs: context.pendingMealIDs,
                    nextPendingMealIndex: context.nextPendingMealIndex,
                  }
                },
                onDone: [
                  {
                    actions: [
                      {
                        type: 'trackMealAdded',
                        params: ({ context, event }) => ({
                          addMealMeta: context.addMealMeta,
                          termID: context.termID,
                          orderEstimate: event.output,
                        }),
                      },
                      'clearAddMealMeta',
                      'removeFirstPendingMeal',
                      'clearNextMaxSelections',
                    ],
                    guard: 'isLastPendingMeal',
                    target: '#menuMachine.checkingSelectionsState',
                  },
                  {
                    actions: [
                      {
                        type: 'trackMealAdded',
                        params: ({ context, event }) => ({
                          addMealMeta: context.addMealMeta,
                          termID: context.termID,
                          orderEstimate: event.output,
                        }),
                      },
                      'clearAddMealMeta',
                      'removeFirstPendingMeal',
                    ],
                    target: '#menuMachine.upgrading.selectingUpgrades',
                  },
                ],
                onError: [
                  {
                    actions: [
                      {
                        type: 'showAddMealError',
                        params: ({ event }) => ({ data: event.error }),
                      },
                      'clearAddMealMeta',
                      'removeFirstPendingMeal',
                      'clearNextMaxSelections',
                    ],
                    guard: 'isLastPendingMeal',
                    target: '#menuMachine.checkingSelectionsState',
                  },
                  {
                    target: '#menuMachine.upgrading.selectingUpgrades',
                  },
                ],
              },
            },
          },
        },
        savingUpgrades: {
          initial: 'updatingTermStatus',
          states: {
            updatingTermStatus: {
              invoke: {
                src: 'updateTermStatus',
                onDone: {
                  actions: ['setNextPendingMealIndex'],
                  target: 'savingNextPendingMeal',
                },
                onError: {
                  target: '#menuMachine.upgrading.selectingUpgrades',
                },
              },
            },
            savingNextPendingMeal: {
              entry: ['setAddMealMetaFromPending'],
              invoke: {
                src: 'savePendingMeal',
                input: ({ context }) => {
                  return {
                    pendingMealIDs: context.pendingMealIDs,
                    nextPendingMealIndex: context.nextPendingMealIndex,
                  }
                },
                onDone: [
                  {
                    actions: [
                      {
                        type: 'trackMealAdded',
                        params: ({ context, event }) => ({
                          addMealMeta: context.addMealMeta,
                          termID: context.termID,
                          orderEstimate: event.output,
                        }),
                      },
                      'clearAddMealMeta',
                      'incrementNextPendingMealIndex',
                    ],
                    guard: 'hasMorePendingMeals',
                    target: 'savingNextPendingMeal',
                    reenter: true,
                  },
                  {
                    actions: [
                      {
                        type: 'trackMealAdded',
                        params: ({ context, event }) => ({
                          addMealMeta: context.addMealMeta,
                          termID: context.termID,
                          orderEstimate: event.output,
                        }),
                      },
                      {
                        type: 'trackFinishedMealSelection',
                        params: ({ context }) => ({
                          termID: context.termID,
                        }),
                      },
                      'clearAddMealMeta',
                      'clearNextMaxSelections',
                      'clearPendingMealIDs',
                      'setNextPendingMealIndex',
                    ],
                    target: '#menuMachine.mealsSelected',
                  },
                ],
                onError: [
                  {
                    actions: [
                      {
                        type: 'showAddMealError',
                        params: ({ event }) => ({ data: event.error }),
                      },
                      'clearAddMealMeta',
                      'incrementNextPendingMealIndex',
                    ],
                    guard: 'hasMorePendingMeals',
                    target: 'savingNextPendingMeal',
                    reenter: true,
                  },
                  {
                    actions: [
                      {
                        type: 'showAddMealError',
                        params: ({ event }) => ({ data: event.error }),
                      },
                      'clearAddMealMeta',
                      'clearNextMaxSelections',
                      'clearPendingMealIDs',
                      'setNextPendingMealIndex',
                    ],
                    target: '#menuMachine.checkingSelectionsState',
                  },
                ],
              },
            },
          },
        },
      },
    },
  },
})
